import { Box, Button, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import EconomicCalendarEventList from "components/EconomicCalendar/EconomicCalendarEventList";
import useEconomicCalendar from "hooks/useEconomicCalendar";
import useCurrentTime from "hooks/useCurrentTime";
import { isCalendarEventPast } from "lib/economicCalendar";

const DashboardEconomicCalendarCard = () => {
  const { t, i18n } = useTranslation("economicCalendar");
  const date = format(new Date(), "yyyy-MM-dd");
  const { items, counts, loading, error, reload } = useEconomicCalendar(date);
  const now = useCurrentTime();

  const visibleItems = items.filter(
    (item) =>
      Number(item.impact) >= 1 && !isCalendarEventPast(item.datetime, now)
  );
  const formattedDate = new Intl.DateTimeFormat(
    i18n.language === "es" ? "es-ES" : "en-GB",
    { weekday: "long", day: "numeric", month: "long" }
  ).format(new Date(`${date}T12:00:00`));

  return (
    <Box
      sx={{
        flex: { lg: "1 1 24%", xs: "1 1 auto" },
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: "100%",
          minHeight: { xs: 300, lg: 0 },
          p: 1.5,
          borderRadius: "10px",
          background:
            "linear-gradient(155deg, rgba(238,243,250,0.96) 0%, #FFFFFF 60%)",
          border: "1px solid #E2E7EF",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={0.7}>
          <Box display="flex" gap={1} minWidth={0} flex={1}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                display: "grid",
                placeItems: "center",
                color: "#FFFFFF",
                background: "#253E66",
                flexShrink: 0,
              }}
            >
              <CalendarMonthIcon fontSize="small" />
            </Box>
            <Box minWidth={0}>
              <Typography fontWeight={800} color="#253E66" lineHeight={1.1}>
                {t("dashboard.title")}
              </Typography>
              <Typography
                variant="caption"
                color="#7B8494"
                textTransform="capitalize"
                noWrap
              >
                {formattedDate}
              </Typography>
            </Box>
          </Box>
          <Button
            component={RouterLink}
            to="/economic-calendar"
            endIcon={<ArrowForwardIcon />}
            size="small"
            variant="contained"
            sx={{
              flexShrink: 0,
              minWidth: 0,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: 10.5,
              fontWeight: 800,
              px: 1.1,
              py: 0.45,
              "& .MuiButton-endIcon": { ml: 0.55 },
              "& .MuiSvgIcon-root": { fontSize: 17 },
            }}
          >
            {t("dashboard.viewAll")}
          </Button>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={0.5}
          mb={1}
        >
          <Button
            component={RouterLink}
            to="/profile#notification-settings"
            startIcon={<NotificationsActiveIcon />}
            size="small"
            sx={{
              minWidth: 0,
              textTransform: "none",
              fontSize: 10.5,
              fontWeight: 800,
              px: 0.7,
              "& .MuiButton-startIcon": { mr: 0.45 },
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            {t("notifications.configure")}
          </Button>
          <Box display="flex" gap={0.5}>
          <Box
            sx={{
              px: 0.8,
              py: 0.35,
              borderRadius: "8px",
              background: "#FDE8E7",
              color: "#A92C2C",
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            {counts.high} {t("short.high")}
          </Box>
          <Box
            sx={{
              px: 0.8,
              py: 0.35,
              borderRadius: "8px",
              background: "#FFF2CC",
              color: "#8A5B00",
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            {counts.medium} {t("short.medium")}
          </Box>
          </Box>
        </Box>

        {error ? (
          <Box flex={1} display="grid" sx={{ placeItems: "center" }} textAlign="center" px={1}>
            <Box>
              <Typography color="error" fontSize={13} fontWeight={700}>
                {t("error")}
              </Typography>
              <Button size="small" startIcon={<RefreshIcon />} onClick={reload}>
                {t("retry")}
              </Button>
            </Box>
          </Box>
        ) : !loading && visibleItems.length === 0 ? (
          <Box
            flex="1 1 0"
            minHeight={0}
            display="grid"
            sx={{ placeItems: "center" }}
            textAlign="center"
            px={2}
          >
            <Typography color="#7B8494" fontSize={13} fontWeight={700}>
              {t("dashboard.noUpcoming")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: "1 1 auto",
              height: 0,
              minHeight: 0,
              overflowX: "hidden",
              overflowY: "auto",
              pr: 0.4,
              scrollbarWidth: "thin",
              scrollbarColor: "#AAB4C3 transparent",
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: "8px",
                background: "#AAB4C3",
              },
            }}
          >
            <EconomicCalendarEventList
              items={visibleItems}
              loading={loading}
              compact
              notificationDate={date}
            />
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default DashboardEconomicCalendarCard;
